import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ClearIcon from '@mui/icons-material/Clear';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import axios from 'axios';
import { getApiBaseUrl, getStoredToken } from '../../services/httpClient';
import {
  CATALOG_THUMB_MAX_INPUT_BYTES,
  CATALOG_THUMB_PX,
  compressImageForUpload,
  toOptimizedThumbDeliveryUrl,
} from '../../utils/compressImageUpload';
import { resolveCategoryThumbSrc } from '../../utils/locsySubcategoryThumbs';

const api = axios.create({
  baseURL: `${getApiBaseUrl()}/taxonomy`,
  timeout: 25000,
});

api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers['Content-Type'] = 'application/json';
  return config;
});

const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'dml5dugde';
const CLOUDINARY_UPLOAD_PRESET =
  process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'locsy_business_profiles';

const PASTELS = [
  ['#FFF4ED', '#FFE8D9'],
  ['#EFF8FF', '#E0F0FF'],
  ['#F3E8FF', '#E8D9FF'],
  ['#E8F5E9', '#DFF5E1'],
  ['#FFF8E1', '#FFECB3'],
  ['#FFEBEE', '#FFCDD2'],
  ['#E0F7FA', '#B2EBF2'],
  ['#FBE9E7', '#FFCCBC'],
];

const emptyForm = () => ({
  name_en: '',
  name_te: '',
  name_hi: '',
  description: '',
  thumbnail_url: '',
  is_active: true,
  category_code: '',
});

const uploadMyTownThumb = async (file) => {
  if (!CLOUDINARY_CLOUD_NAME || !CLOUDINARY_UPLOAD_PRESET) {
    throw new Error('Cloudinary is not configured.');
  }
  if (file.size > CATALOG_THUMB_MAX_INPUT_BYTES) {
    throw new Error('Image too large (max 12 MB).');
  }
  const prepared = await compressImageForUpload(file, { size: CATALOG_THUMB_PX });
  const formData = new FormData();
  formData.append('file', prepared);
  formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
  formData.append('folder', 'mytown_category_thumbs');
  const cleanAxios = axios.create();
  if (cleanAxios.defaults.headers.common) {
    delete cleanAxios.defaults.headers.common.Authorization;
    delete cleanAxios.defaults.headers.common.authorization;
  }
  const response = await cleanAxios.post(
    `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
    formData,
  );
  const secureUrl = response?.data?.secure_url || response?.data?.url || '';
  if (!secureUrl) throw new Error('Thumbnail upload failed');
  return toOptimizedThumbDeliveryUrl(secureUrl);
};

const thumbForSub = (sub) =>
  resolveCategoryThumbSrc({
    thumbnail_url: sub?.thumbnail_url,
    categoryKey: sub?.code,
    subCategoryId: sub?.code,
    mainCategoryId: sub?.category_code,
    label: sub?.name_en,
  });

function SortableCard({ sub, index, onEdit, onToggleActive }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: String(sub.id),
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
  };
  const [c0, c1] = PASTELS[index % PASTELS.length];
  const src = thumbForSub(sub);
  const active = sub.is_active !== false;

  return (
    <Grid item xs={12} sm={6} md={4} lg={3} ref={setNodeRef} style={style}>
      <Card
        sx={{
          height: '100%',
          borderRadius: 2.5,
          border: active ? '1px solid rgba(15,23,42,0.08)' : '1px dashed rgba(148,163,184,0.7)',
          background: `linear-gradient(160deg, ${c0}, ${c1})`,
          opacity: active ? 1 : 0.72,
        }}
      >
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <IconButton size="small" {...attributes} {...listeners} sx={{ cursor: 'grab' }}>
                <DragIndicatorIcon fontSize="small" />
              </IconButton>
              <Chip size="small" label={`#${index + 1}`} sx={{ height: 22, fontWeight: 700 }} />
            </Stack>
            <Stack direction="row" spacing={0.25}>
              <Tooltip title={active ? 'Hide on MyTown' : 'Show on MyTown'}>
                <IconButton size="small" onClick={() => onToggleActive(sub)}>
                  {active ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit labels & image">
                <IconButton size="small" onClick={() => onEdit(sub)}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
          <Box
            sx={{
              width: '100%',
              aspectRatio: '1 / 1',
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: '#fff',
              mb: 1,
              border: '1px solid rgba(15,23,42,0.06)',
            }}
          >
            {src ? (
              <Box
                component="img"
                src={src}
                alt={sub.name_en}
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            ) : (
              <Box
                sx={{
                  height: '100%',
                  display: 'grid',
                  placeItems: 'center',
                  color: 'text.secondary',
                  fontSize: 12,
                }}
              >
                No image
              </Box>
            )}
          </Box>
          <Typography variant="subtitle2" fontWeight={800} noWrap title={sub.name_en}>
            {sub.name_en || sub.code}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {sub.code} · {sub.category_code}
          </Typography>
          {!active && (
            <Chip size="small" label="Hidden" color="warning" sx={{ mt: 0.75, height: 20 }} />
          )}
        </CardContent>
      </Card>
    </Grid>
  );
}

const MyTownScreens = () => {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [pageSearch, setPageSearch] = useState('');
  const [orderDirty, setOrderDirty] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [thumbUploading, setThumbUploading] = useState(false);
  const [activeDragId, setActiveDragId] = useState(null);
  const thumbInputRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const loadTree = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/admin/overview');
      if (!res.data?.success) {
        setError(res.data?.message || 'Failed to load MyTown categories');
        setTree([]);
        return;
      }
      const rows = (res.data.data || []).map((cat, idx) => ({
        ...cat,
        sort_order: Number.isFinite(Number(cat.sort_order)) ? Number(cat.sort_order) : idx,
        subcategories: (cat.subcategories || [])
          .map((s, i) => ({
            ...s,
            category_code: s.category_code || cat.code,
            sort_order: Number.isFinite(Number(s.sort_order)) ? Number(s.sort_order) : i,
          }))
          .sort((a, b) => a.sort_order - b.sort_order || String(a.name_en).localeCompare(String(b.name_en))),
      }));
      rows.sort((a, b) => a.sort_order - b.sort_order || String(a.name_en).localeCompare(String(b.name_en)));
      setTree(rows);
      setOrderDirty(false);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load');
      setTree([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  const sortedCategories = useMemo(
    () => [...tree].sort((a, b) => a.sort_order - b.sort_order),
    [tree],
  );

  const flatSubs = useMemo(
    () => sortedCategories.flatMap((c) => c.subcategories || []),
    [sortedCategories],
  );

  const workingSubs = useMemo(() => {
    if (selectedCategory === 'all') return flatSubs;
    const cat = sortedCategories.find((c) => c.code === selectedCategory);
    return cat?.subcategories || [];
  }, [selectedCategory, flatSubs, sortedCategories]);

  const pageQuery = String(pageSearch || '').trim().toLowerCase();
  const displaySubs = useMemo(() => {
    if (!pageQuery) return workingSubs;
    return workingSubs.filter((s) => {
      const hay = [s.name_en, s.name_te, s.name_hi, s.code, s.category_code, s.description]
        .map((x) => String(x || '').toLowerCase())
        .join(' ');
      return hay.includes(pageQuery);
    });
  }, [workingSubs, pageQuery]);

  const stats = useMemo(() => {
    const visible = flatSubs.filter((s) => s.is_active !== false).length;
    const hidden = flatSubs.length - visible;
    const missingThumb = flatSubs.filter((s) => !String(s.thumbnail_url || '').trim()).length;
    return {
      categories: sortedCategories.length,
      cards: flatSubs.length,
      visible,
      hidden,
      missingThumb,
    };
  }, [flatSubs, sortedCategories]);

  const activeDragSub = useMemo(
    () => flatSubs.find((s) => String(s.id) === String(activeDragId)) || null,
    [flatSubs, activeDragId],
  );

  const renumberSubsInCategory = (categoryCode, list) =>
    tree.map((cat) => {
      if (cat.code !== categoryCode) return cat;
      return {
        ...cat,
        subcategories: list.map((s, i) => ({ ...s, sort_order: i, category_code: categoryCode })),
      };
    });

  const moveCategory = (code, dir) => {
    const list = [...sortedCategories];
    const idx = list.findIndex((c) => c.code === code);
    if (idx < 0) return;
    const next = idx + dir;
    if (next < 0 || next >= list.length) return;
    const moved = arrayMove(list, idx, next).map((c, i) => ({ ...c, sort_order: i }));
    setTree(moved);
    setOrderDirty(true);
  };

  const onDragEnd = (event) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    if (selectedCategory === 'all') {
      setSnack({
        severity: 'info',
        message: 'Pick a parent group to reorder cards. “All” mirrors app order (groups × cards).',
      });
      return;
    }
    if (pageQuery) {
      setSnack({ severity: 'warning', message: 'Clear search before reordering.' });
      return;
    }
    const list = [...workingSubs];
    const oldIndex = list.findIndex((s) => String(s.id) === String(active.id));
    const newIndex = list.findIndex((s) => String(s.id) === String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const moved = arrayMove(list, oldIndex, newIndex);
    setTree(renumberSubsInCategory(selectedCategory, moved));
    setOrderDirty(true);
  };

  const toggleActiveLocal = (sub) => {
    setTree((prev) =>
      prev.map((cat) => ({
        ...cat,
        subcategories: (cat.subcategories || []).map((s) =>
          String(s.id) === String(sub.id) ? { ...s, is_active: s.is_active === false } : s,
        ),
      })),
    );
    setOrderDirty(true);
  };

  const openEdit = (sub) => {
    setEditing(sub);
    setForm({
      name_en: sub.name_en || '',
      name_te: sub.name_te || '',
      name_hi: sub.name_hi || '',
      description: sub.description || '',
      thumbnail_url: sub.thumbnail_url || '',
      is_active: sub.is_active !== false,
      category_code: sub.category_code || '',
    });
    setDialogOpen(true);
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setThumbUploading(true);
    try {
      const url = await uploadMyTownThumb(file);
      setForm((f) => ({ ...f, thumbnail_url: url }));
      setSnack({
        severity: 'success',
        message: `Uploaded & cropped to ${CATALOG_THUMB_PX}×${CATALOG_THUMB_PX}.`,
      });
    } catch (e) {
      setSnack({ severity: 'error', message: e.message || 'Upload failed' });
    } finally {
      setThumbUploading(false);
      if (thumbInputRef.current) thumbInputRef.current.value = '';
    }
  };

  const handleSaveCard = async () => {
    if (!editing?.id) return;
    if (!String(form.name_en || '').trim()) {
      setSnack({ severity: 'warning', message: 'English name is required.' });
      return;
    }
    setSaving(true);
    try {
      const res = await api.put(`/admin/subcategories/${encodeURIComponent(editing.id)}`, {
        name_en: form.name_en,
        name_te: form.name_te || null,
        name_hi: form.name_hi || null,
        description: form.description || null,
        thumbnail_url: form.thumbnail_url || null,
        is_active: form.is_active,
        category_code: form.category_code || undefined,
      });
      if (!res.data?.success) {
        setSnack({ severity: 'error', message: res.data?.message || 'Save failed' });
        return;
      }
      setSnack({
        severity: 'success',
        message: 'Category card updated — Citizen MyTown refreshes on next open / ~30s.',
      });
      setDialogOpen(false);
      await loadTree();
    } catch (e) {
      setSnack({ severity: 'error', message: e.response?.data?.message || e.message });
    } finally {
      setSaving(false);
    }
  };

  const saveOrder = async () => {
    setBulkSaving(true);
    try {
      const categories = sortedCategories.map((c, i) => ({
        code: c.code,
        sort_order: i,
        is_active: c.is_active !== false,
      }));
      const subcategories = sortedCategories.flatMap((c) =>
        (c.subcategories || []).map((s, i) => ({
          id: s.id,
          sort_order: Number.isFinite(Number(s.sort_order)) ? Number(s.sort_order) : i,
          is_active: s.is_active !== false,
        })),
      );
      const res = await api.put('/admin/mytown/order', { categories, subcategories });
      if (!res.data?.success) {
        setSnack({ severity: 'error', message: res.data?.message || 'Order save failed' });
        return;
      }
      setSnack({ severity: 'success', message: 'MyTown order & visibility saved.' });
      setOrderDirty(false);
      if (Array.isArray(res.data.data)) {
        const rows = res.data.data.map((cat, idx) => ({
          ...cat,
          sort_order: Number.isFinite(Number(cat.sort_order)) ? Number(cat.sort_order) : idx,
          subcategories: (cat.subcategories || [])
            .map((s, i) => ({
              ...s,
              category_code: s.category_code || cat.code,
              sort_order: Number.isFinite(Number(s.sort_order)) ? Number(s.sort_order) : i,
            }))
            .sort((a, b) => a.sort_order - b.sort_order),
        }));
        rows.sort((a, b) => a.sort_order - b.sort_order);
        setTree(rows);
      } else {
        await loadTree();
      }
    } catch (e) {
      setSnack({ severity: 'error', message: e.response?.data?.message || e.message });
    } finally {
      setBulkSaving(false);
    }
  };

  const previewSrc =
    form.thumbnail_url ||
    (editing
      ? thumbForSub({ ...editing, thumbnail_url: form.thumbnail_url })
      : '');

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        spacing={2}
        mb={2}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <PhoneIphoneIcon color="warning" />
            <Typography variant="h5" fontWeight={800}>
              MyTown Screens
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Control the Locsy Citizen <strong>MyTown</strong> category grid — card images, EN/TE/HI
            labels, show/hide, and order. Same data the app loads from taxonomy.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Button startIcon={<RefreshIcon />} onClick={loadTree} disabled={loading}>
            Refresh
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={bulkSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            disabled={!orderDirty || bulkSaving}
            onClick={saveOrder}
          >
            Save order / visibility
          </Button>
        </Stack>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        Live URL (AA login): <strong>https://services.knls.in/mytownscreens</strong> — also listed
        under Admin Control Screens → Screens Controls. App “All” order = parent group order × card
        order inside each group.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Chip label={`${stats.categories} groups`} />
        <Chip label={`${stats.cards} cards`} color="primary" variant="outlined" />
        <Chip label={`${stats.visible} visible`} color="success" variant="outlined" />
        <Chip label={`${stats.hidden} hidden`} color="warning" variant="outlined" />
        <Chip label={`${stats.missingThumb} using CDN fallback`} variant="outlined" />
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <Card sx={{ borderRadius: 2, position: { md: 'sticky' }, top: { md: 88 } }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={800} mb={1}>
                Parent groups
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                Up/down changes the MyTown “All” sequence of whole groups.
              </Typography>
              <Stack spacing={0.75}>
                <Button
                  size="small"
                  variant={selectedCategory === 'all' ? 'contained' : 'outlined'}
                  color="warning"
                  onClick={() => setSelectedCategory('all')}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  All (app order)
                </Button>
                {sortedCategories.map((cat, idx) => (
                  <Stack
                    key={cat.code}
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{
                      borderRadius: 1.5,
                      border:
                        selectedCategory === cat.code
                          ? '1px solid #f59e0b'
                          : '1px solid transparent',
                      bgcolor: selectedCategory === cat.code ? 'rgba(245,158,11,0.08)' : 'transparent',
                      px: 0.5,
                    }}
                  >
                    <Button
                      size="small"
                      onClick={() => setSelectedCategory(cat.code)}
                      sx={{
                        flex: 1,
                        justifyContent: 'flex-start',
                        textTransform: 'none',
                        fontWeight: 700,
                        color: cat.is_active === false ? 'text.disabled' : 'text.primary',
                      }}
                    >
                      {idx + 1}. {cat.name_en || cat.code}
                      <Typography component="span" variant="caption" color="text.secondary" ml={0.75}>
                        ({(cat.subcategories || []).length})
                      </Typography>
                    </Button>
                    <IconButton size="small" onClick={() => moveCategory(cat.code, -1)} disabled={idx === 0}>
                      <ArrowUpwardIcon fontSize="inherit" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => moveCategory(cat.code, 1)}
                      disabled={idx === sortedCategories.length - 1}
                    >
                      <ArrowDownwardIcon fontSize="inherit" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={9}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ sm: 'center' }}
            mb={2}
          >
            <TextField
              size="small"
              fullWidth
              placeholder="Search Kirana, stationery, fancy…"
              value={pageSearch}
              onChange={(e) => setPageSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: pageSearch ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setPageSearch('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            {orderDirty && (
              <Chip color="warning" label="Unsaved order / visibility" sx={{ fontWeight: 700 }} />
            )}
          </Stack>

          {selectedCategory === 'all' && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Preview of MyTown “All” tab. Select a parent group on the left to drag-reorder its
              cards.
            </Alert>
          )}

          {loading ? (
            <Box sx={{ py: 8, display: 'grid', placeItems: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={(e) => setActiveDragId(e.active.id)}
              onDragEnd={onDragEnd}
              onDragCancel={() => setActiveDragId(null)}
            >
              <SortableContext
                items={displaySubs.map((s) => String(s.id))}
                strategy={rectSortingStrategy}
              >
                <Grid container spacing={1.5}>
                  {displaySubs.map((sub, index) => (
                    <SortableCard
                      key={sub.id}
                      sub={sub}
                      index={index}
                      onEdit={openEdit}
                      onToggleActive={toggleActiveLocal}
                    />
                  ))}
                </Grid>
              </SortableContext>
              <DragOverlay>
                {activeDragSub ? (
                  <Card sx={{ width: 220, borderRadius: 2, opacity: 0.95 }}>
                    <CardContent>
                      <Typography fontWeight={800}>{activeDragSub.name_en}</Typography>
                    </CardContent>
                  </Card>
                ) : null}
              </DragOverlay>
            </DndContext>
          )}

          {!loading && displaySubs.length === 0 && (
            <Alert severity="warning">No category cards match this filter.</Alert>
          )}
        </Grid>
      </Grid>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit MyTown card</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={0.5}>
            <Box
              sx={{
                width: 140,
                height: 140,
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid rgba(15,23,42,0.1)',
                alignSelf: 'center',
                bgcolor: '#f8fafc',
              }}
            >
              {previewSrc ? (
                <Box
                  component="img"
                  src={previewSrc}
                  alt=""
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : null}
            </Box>
            <Stack direction="row" spacing={1} justifyContent="center">
              <Button
                component="label"
                startIcon={
                  thumbUploading ? <CircularProgress size={16} /> : <CloudUploadOutlinedIcon />
                }
                disabled={thumbUploading || saving}
                variant="outlined"
              >
                Upload image
                <input
                  ref={thumbInputRef}
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleUpload(e.target.files?.[0])}
                />
              </Button>
              <Button
                startIcon={<RestartAltIcon />}
                disabled={saving || !form.thumbnail_url}
                onClick={() => setForm((f) => ({ ...f, thumbnail_url: '' }))}
              >
                Clear CMS image
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" textAlign="center">
              Recommended: square 512×512 px (1:1). Any upload is center-cropped to
              512×512. Clearing falls back to CDN / built-in thumb.
            </Typography>
            <Divider />
            <TextField
              label="Name (English)"
              value={form.name_en}
              onChange={(e) => setForm((f) => ({ ...f, name_en: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Name (Telugu)"
              value={form.name_te}
              onChange={(e) => setForm((f) => ({ ...f, name_te: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Name (Hindi)"
              value={form.name_hi}
              onChange={(e) => setForm((f) => ({ ...f, name_hi: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Description / notes"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Parent group</InputLabel>
              <Select
                label="Parent group"
                value={form.category_code || ''}
                onChange={(e) => setForm((f) => ({ ...f, category_code: e.target.value }))}
              >
                {sortedCategories.map((c) => (
                  <MenuItem key={c.code} value={c.code}>
                    {c.name_en || c.code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                />
              }
              label="Visible on MyTown"
            />
            <TextField
              label="Image URL (optional paste)"
              value={form.thumbnail_url}
              onChange={(e) => setForm((f) => ({ ...f, thumbnail_url: e.target.value }))}
              fullWidth
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleSaveCard}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          >
            Save card
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? (
          <Alert severity={snack.severity || 'info'} onClose={() => setSnack(null)} variant="filled">
            {snack.message}
          </Alert>
        ) : null}
      </Snackbar>
    </Container>
  );
};

export default MyTownScreens;
